import React, { useEffect, useRef } from 'react';
import { Chart } from '@antv/g2';
import { PriceHistoryChartData } from '../types/company';

interface PriceHistoryChartProps {
  chartData: PriceHistoryChartData;
}

// interface TooltipItem {
//   name: string;
//   value: number;
//   data: {
//     date: string;
//     value: number;
//     type: string;
//   };
// }

// interface FundMark {
//   price: number;
//   tickers: string;
// }
//
// interface FundingRound {
//   roundName: string;
//   stockPrice: number;
//   amount: number;
//   valuation: number;
// }

// interface ChartData {
//   date: string;
//   value: number | null;
//   type: string;
// }
//
// interface EstValuations {
//   [key: string]: number;
// }
//
// interface FundMarks {
//   [key: string]: FundMark;
// }
//
// interface FundingRounds {
//   [key: string]: FundingRound;
// }

const PriceHistoryChart: React.FC<PriceHistoryChartProps> = ({ chartData }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  const colors = ['#0f4a62', '#f68935', '#934893'];
  const eMountMap: Record<string, string> = {
    '01': 'Jan', '02': 'Feb', '03': 'Mar', '04': 'Apr', '05': 'May', '06': 'Jun',
    '07': 'Jul', '08': 'Aug', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Dec'
  };

  const formatDollar = (value: number | null, position?: number, unit?: string) => {
    if (value === null) return '-';
    if (position) {
      return `$${(value / position).toFixed(2)}${unit || ''}`;
    }
    return `$${value.toFixed(2)}${unit || ''}`;
  };

  // const formatCurrency = (v: number | string, precision?: number) => {
  //   if (v === '') return '';
  //   const num = precision ? Number(v).toFixed(precision) : Number(v).toString();
  //   return num.replace(/(\d{1,3})(?=(?:\d{3})+(?!\d))/g, '$1,');
  // };

  const generateData = (data: Record<string, any>, dates: string[], flag?: string) => {
    return dates.map(date => {
      if (flag && data) {
        return data[date] && data[date][flag] ? data[date][flag] : null;
      }
      return data?.[date] || null;
    });
  };

  useEffect(() => {
    if (!chartRef.current || !chartData.dates.length) return;

    // 销毁之前的图表实例
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // 创建新的图表实例
    const chart = new Chart({
      container: chartRef.current,
      autoFit: true,
      height: 400,
    });

    // 准备数据
    const { dates, names, datas } = chartData;
    const seriesData = names.map((name) => {
      if (['Funding Rounds', 'Fund Marks'].includes(name)) {
        const data = name === 'Funding Rounds'
          ? generateData(datas.fundingRounds, dates, 'stockPrice')
          : generateData(datas.fundMarks, dates, 'price');

        return {
          name,
          type: 'point',
          data: dates.map((date, i) => ({
            date,
            value: data[i],
            type: name
          }))
        };
      } else {
        const data = generateData(datas.prices, dates);
        return {
          name,
          type: 'line',
          data: dates.map((date, i) => ({
            date,
            value: data[i],
            type: name
          }))
        };
      }
    });

    // 合并所有数据
    const allData = seriesData.flatMap(series => series.data);

    // 配置图表
    chart.data(allData);

    // 配置坐标轴
    chart.scale({
      date: {
        type: 'cat',
        formatter: (date: string) => {
          const [year, month, day] = date.split('-');
          return `${eMountMap[month] || month} ${day}, ${year}`;
        }
      },
      value: {
        nice: true,
        formatter: (val: number) => formatDollar(val)
      }
    });

    // 配置图例
    chart.legend({
      position: 'bottom',
      itemName: {
        style: {
          fill: '#666666'
        }
      }
    });

    chart.interaction('tooltip', false)
    // 配置提示框
    // chart.interaction('tooltip', {
    //   render: (event: any) => {
    //     const { title, items } = event.data;
    //     const date = title;
    //     const [year, month, day] = date.split('-');
    //     let content = `<div style="font-weight: 700;">${eMountMap[month]} ${day}, ${year}</div>`;
    //
    //     items?.forEach((item: TooltipItem) => {
    //       const seriesName = item.name;
    //       const value = item.value;
    //
    //       content += `<div style="font-weight: 700;">${seriesName}</div>`;
    //
    //       if (seriesName === 'Augment Price') {
    //         const estValuations = datas.estValuations as unknown as EstValuations;
    //         const estValuation = estValuations[date];
    //         content += `
    //           <div style="display: flex; align-items: center; justify-content: space-between;">
    //             <span>Price：</span>
    //             <span style="font-weight: 700;">${formatDollar(value)}</span>
    //           </div>
    //           <div style="display: flex; align-items: center; justify-content: space-between;">
    //             <span>Est. Valuation：</span>
    //             <span style="font-weight: 700;">${formatDollar(estValuation, 1e9, 'B')}</span>
    //           </div>
    //         `;
    //       } else if (seriesName === 'Fund Marks') {
    //         const fundMarks = datas.fundMarks as unknown as FundMarks;
    //         const fundMark = fundMarks[date];
    //         content += `
    //           <div style="display: flex; align-items: center; justify-content: space-between;">
    //             <span>Price：</span>
    //             <span style="font-weight: 700;">${formatDollar(fundMark?.price)}</span>
    //           </div>
    //           <div style="display: flex; align-items: center; justify-content: space-between;">
    //             <span>Est. Valuation：</span>
    //             <span style="font-weight: 700;">${fundMark?.tickers || '-'}</span>
    //           </div>
    //         `;
    //       } else if (seriesName === 'Funding Rounds') {
    //         const fundingRounds = datas.fundingRounds as unknown as FundingRounds;
    //         const round = fundingRounds[date];
    //         content += `
    //           <div style="display: flex; align-items: center; justify-content: space-between;">
    //             <span>Round：</span>
    //             <span style="font-weight: 700;">${round?.roundName || '-'}</span>
    //           </div>
    //           <div style="display: flex; align-items: center; justify-content: space-between;">
    //             <span>Share Price：</span>
    //             <span style="font-weight: 700;">${formatDollar(round?.stockPrice)}</span>
    //           </div>
    //           <div style="display: flex; align-items: center; justify-content: space-between;">
    //             <span>Size：</span>
    //             <span style="font-weight: 700;">${formatCurrency(round?.amount, 2)}</span>
    //           </div>
    //           <div style="display: flex; align-items: center; justify-content: space-between;">
    //             <span>Est. Valuation：</span>
    //             <span style="font-weight: 700;">${formatDollar(round?.valuation, 1e9, 'B')}</span>
    //           </div>
    //         `;
    //       }
    //     });
    //
    //     return content;
    //   },
    //   hide: () => {
    //     return true;
    //   }
    // });

    // 绘制图形
    seriesData.forEach((series, idx) => {
      if (series.type === 'line') {
        chart.line()
          .encode('x', 'date')
          .encode('y', 'value')
          .encode('color', 'type')
          .style('stroke', colors[idx])
          .style('lineWidth', 2)
          .label({
            text: 'value',
            formatter: (val: number) => formatDollar(val)
          });
      } else {
        chart.point()
          .encode('x', 'date')
          .encode('y', 'value')
          .encode('color', 'type')
          .style('fill', colors[idx])
          .style('r', 4)
          .style('shape', 'circle')
          .label({
            text: 'value',
            formatter: (val: number) => formatDollar(val)
          });
      }
    });

    // 渲染图表
    chart.render();
    chartInstance.current = chart;

    // 清理函数
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [chartData]);

  return <div ref={chartRef} style={{ width: '100%', height: '400px' }} />;
};

export default PriceHistoryChart;
